import { InfoCircleOutlined } from "@ant-design/icons";
import type { DesignScopeImportLogStats } from "./import-log-result";

type ImportLogSummaryPanelProps = {
  stats: DesignScopeImportLogStats;
  title?: string;
  description?: string;
  onBack?: () => void;
  backLabel?: string;
};

function SummaryRow({ label, value }: { label: string; value: number }) {
  return (
    <>
      <div>{label}</div>
      <div className="text-right font-semibold">{value}</div>
    </>
  );
}

function ImportLogSummaryPanel({
  stats,
  title = "Import Summary",
  description = "Import complete. Review the summary below.",
  onBack,
  backLabel = "Back"
}: ImportLogSummaryPanelProps) {
  return (
    <div data-testid="design-scope-import-summary">
      {onBack && (
        <button
          type="button"
          className="mb-4 flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-ciq-primary-800"
          onClick={onBack}
          data-testid="design-scope-import-summary-back"
        >
          <span className="font-semibold">{backLabel}</span>
        </button>
      )}
      <p className="mb-1 text-base font-semibold">{title}</p>
      <p className="mb-3 text-xs text-black text-opacity-50">{description}</p>
      <div
        className="grid w-full grid-cols-[1fr_auto] gap-x-4 gap-y-2 whitespace-nowrap rounded p-4"
        style={{ backgroundColor: "var(--ciq-neutral-25)" }}
      >
        <SummaryRow label="Total" value={stats.Total ?? 0} />
        <SummaryRow label="Added" value={stats.Imported ?? 0} />
        <SummaryRow label="Updated" value={stats.Updated ?? 0} />
        {stats.Corrected !== undefined && (
          <SummaryRow label="Corrected" value={stats.Corrected ?? 0} />
        )}
        {(stats.AddedUpdatedWithErrors !== undefined ||
          stats.Unchanged !== undefined) && (
          <>
            <SummaryRow
              label="Added/Updated With Errors"
              value={stats.AddedUpdatedWithErrors ?? 0}
            />
            <SummaryRow label="Unchanged" value={stats.Unchanged ?? 0} />
          </>
        )}
        <SummaryRow label="Failed" value={stats.Failed ?? 0} />
      </div>
      <p className="mt-4 flex items-start gap-2 text-xs text-black text-opacity-60">
        <InfoCircleOutlined className="mt-0.5 shrink-0" />
        <span>
          In case of Failure/Error a failure report will automatically be
          downloaded when available.
        </span>
      </p>
    </div>
  );
}

export default ImportLogSummaryPanel;
